import React, {lazy, Suspense, useState} from 'react';
import styled from 'styled-components';
import {useMessages} from "i18n";
import {preventClickPropagation} from "utils/util";
import Email, {useEmailValidation, validateEmail} from "../components/Email";
import {EmailSuggestionChip} from "../components/emailSuggestion/EmailSuggestionChip";
import {useUser} from "UserProvider";
import Chips from "../components/Chips";
import {goToPricing, useHistory} from "utils/HistoryUtils";
import {Grid} from "@mui/material";
import {recipientLimit} from "./gating";

// Lazily loaded so its bundled too_many.svg (~45 KB) leaves the eager landing graph (perf #97). It
// only mounts content when the recipient limit is exceeded (a rare interaction), so a null fallback
// is invisible in practice.
const UpgradePopover = lazy(() => import("../components/UpgradePopover"));

const Root = styled(Grid)`
	min-width: 100%;
`

export default function EmailComponent({onEmailsTo, onEmailFrom, onError, recipientLabel, plan, onUpgrade,
	showSender = true}) {
	let [user] = useUser();
	const {sender} = useMessages();
	const toLabel = recipientLabel ?? sender.emailInstead.sendTo;
	let [emails, setEmails] = useState([]);
	let [emailTo, setEmailTo] = useState("");
	let [errorEmail, checkEmail] = useEmailValidation();
	let history = useHistory();
	let [emailFrom, setEmailFrom] = useState("");
	let [errorEmailFrom, checkEmailFrom] = useEmailValidation();
	const [openPopover, setOpenPopover] = useState(false);
	
	let onChangeEmailFrom = e => {
		preventClickPropagation(e)
		const value = e.target.value;
		setEmailFrom(value);
		checkEmailFrom(value);
		onEmailFrom(value)
	};
	let onChangeEmail = e => {
		preventClickPropagation(e)
		const value = e.target.value;
		setEmailTo(value);
		checkEmail(value);
	};

	// Tapping a "did you mean" chip (ui#1959) has to run the same path as typing, so validation and the
	// parent's onEmailFrom see the corrected address rather than the typo they were last told about.
	let acceptSuggestedFrom = corrected => {
		setEmailFrom(corrected);
		checkEmailFrom(corrected);
		onEmailFrom(corrected);
	};
	let acceptSuggestedTo = corrected => {
		setEmailTo(corrected);
		checkEmail(corrected);
	};
	let handleDelete = email => {
		let i = emails.indexOf(email);
		if (i < 0) {
			return
		}
		let tmp = [...emails];
		tmp.splice(i, 1);
		
		setEmails(tmp);
		onEmailsTo(tmp);
	};
	
	let addNewEmail = e => {
		preventClickPropagation(e)
		let email = e.target.value.trim().toLowerCase();
		if (!validateEmail(email)) {
			// Invalid/empty values must not enter the array: closing the limit popover triggers blur again,
			// and retaining the value would recursively reopen it at any catalogue-defined plan boundary.
			return;
		}
		
		if (emails.includes(email)) {
			// if email is already in list, clear entered email, user doesn't need to be alerted
			setEmailTo("");
			return;
		}
		
		const newEmails = [email, ...emails];
		const limit = recipientLimit(plan);

		if (newEmails.length > limit) {
			setEmailTo("");
			setOpenPopover(true);
			onUpgrade?.();
			return;
		}
		
		setEmails(newEmails);
		setEmailTo("");
		onEmailsTo(newEmails);
	}
	
	onError((showSender && errorEmailFrom) || errorEmail);
	return (
        <Root onClick={e => preventClickPropagation(e)}
                     spacing={1}
                     container
                     alignItems={"stretch"}
                     alignContent={"stretch"}
                     direction={"column"}
        >
			{showSender && <Grid>
				<Email
                    value={emailFrom}
                    margin='dense'
                    onChange={onChangeEmailFrom}
                    error={errorEmailFrom}
                    variant="standard"
                    label={sender.senderEmail}
                    onKeyPress={e => {
                        preventClickPropagation(e)
                        const email = e.target.value.trim().toLowerCase();
                        setEmailFrom(email);
                    }}
                    onClick={e => preventClickPropagation(e)}/>
				<EmailSuggestionChip value={emailFrom} onAccept={acceptSuggestedFrom}/>
			</Grid>}
            <Grid>
                <Email
                    value={emailTo}
                    margin='dense'
                    onChange={onChangeEmail}
                    error={errorEmail}
                    variant="standard"
                    label={toLabel}
                    onBlur={e => {
                        preventClickPropagation(e)
                        addNewEmail(e)
                    }}
                    onKeyPress={e => {
                        if (e.key === 'Enter') {
                            addNewEmail(e)
                        }
                    }}
                    onClick={e => preventClickPropagation(e)}/>
                <EmailSuggestionChip value={emailTo} onAccept={acceptSuggestedTo}/>
                <Chips list={emails} handleDelete={handleDelete}/>
            </Grid>
            
            <Suspense fallback={null}>
                <UpgradePopover
                    open={openPopover}
                    handleClose={() => setOpenPopover(false)}
                    title={sender.emailInstead.upgradeTitle}
					message={sender.emailInstead.upgradeMessage}
					buttonMessage={sender.emailInstead.viewPaidPlans}
                    onButtonClick={e => goToPricing(history)}
                />
            </Suspense>
        </Root>
    );
};
